import React, { useState, useEffect } from "react";
import { getPublishedResources } from "../../firebase";
import { Resource } from "../../types";
import { format } from "date-fns";
import { BookOpenIcon, XMarkIcon } from "@heroicons/react/24/outline";

const formatDate = (timestamp: number) => {
  return format(new Date(timestamp), "MMM d, yyyy");
};

const stripHtml = (html: string) => {
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
};

const ResourceDetailDialog: React.FC<{
  resource: Resource | null;
  open: boolean;
  onClose: () => void;
}> = ({ resource, open, onClose }) => {
  if (!resource) return null;

  return (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto ${
        open ? "block" : "hidden"
      }`}
    >
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-start">
              <h3 className="text-2xl font-bold text-gray-900">
                {resource.title}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {resource.imageURL && (
              <div className="mt-4 mb-6 rounded-lg overflow-hidden">
                <img
                  src={resource.imageURL}
                  alt={resource.title}
                  className="w-full h-64 object-cover"
                />
              </div>
            )}

            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: resource.body }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Resources: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const data = await getPublishedResources();
        setResources(data);
      } catch (err: any) {
        console.error("Error loading resources:", err);
        setError("Failed to load resources. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  const handleOpenResource = (resource: Resource) => {
    setSelectedResource(resource);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <div className="container mx-auto px-4">
      <h1 className="text-3xl font-bold mb-2">Resources</h1>
      <p className="text-gold font-josefin text-xl mb-5">
        Educational materials and helpful guides
      </p>

      {loading && (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4">
          {error}
        </div>
      )}

      {!loading && !error && resources.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
            <BookOpenIcon className="h-6 w-6 text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Resources Available
          </h3>
          <p className="text-gray-500">
            Check back soon for new resources and articles.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {resources.map((resource) => (
            <div
              key={resource.id}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer flex flex-col h-full"
              onClick={() => handleOpenResource(resource)}
            >
              {resource.imageURL ? (
                <img
                  src={resource.imageURL}
                  alt={resource.title}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-blue-500 flex items-center justify-center">
                  <BookOpenIcon className="h-12 w-12 text-white" />
                </div>
              )}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl font-semibold mb-2">{resource.title}</h3>

                <p className="text-gray-500 mb-4 text-sm">
                  {stripHtml(resource.body).substring(0, 120)}
                  {stripHtml(resource.body).length > 120 ? "..." : ""}
                </p>

                <div className="mt-auto pt-4">
                  <p className="text-sm text-gray-500">
                    Published: {formatDate(resource.createdAt)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ResourceDetailDialog
        resource={selectedResource}
        open={dialogOpen}
        onClose={handleCloseDialog}
      />
    </div>
  );
};

export default Resources;
